import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  studioActions,
  sel,
  clamp,
  fitImage,
  CANVAS_PAD,
  SIZER_PAD,
} from '../../store/studioStore';
import { drawRulerTicks } from '../../utils/canvasUtils';

// ─────────────────────────────────────────────────────────────────────────────
// Element node (rendered as div)
// ─────────────────────────────────────────────────────────────────────────────
function ElementNode({
  item,
  isSelected,
  selectionCount,
  onMouseDown,
  db,
  onDoubleClick,
  onTextChange,
  onTextBlur,
}) {
  const textEditRef = React.useRef(null);

  // Focus when entering edit mode
  React.useEffect(() => {
    if (item.editing && textEditRef.current) {
      textEditRef.current.focus();
      // Place cursor at end
      const range = document.createRange();
      const sel = window.getSelection();
      const textNode = textEditRef.current.firstChild;

      if (textNode && textNode.nodeType === Node.TEXT_NODE) {
        range.setStart(textNode, textNode.length);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  }, [item.editing]);

  // Restore cursor position after state update
  React.useEffect(() => {
    if (
      item.editing &&
      textEditRef.current &&
      textEditRef.current._cursorPos !== undefined
    ) {
      const range = document.createRange();
      const sel = window.getSelection();
      const textNode = textEditRef.current.firstChild;

      if (textNode && textNode.nodeType === Node.TEXT_NODE) {
        const pos = Math.min(textEditRef.current._cursorPos, textNode.length);
        range.setStart(textNode, pos);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }

      delete textEditRef.current._cursorPos;
    }
  });

  const resolveText = () => {
    if (item.dbField && db?.fields?.includes(item.dbField)) {
      const rec = db.records?.[db.currentRecord];
      if (rec?.[item.dbField] !== undefined) return String(rec[item.dbField]);
    } else {
      return item.dbField || '';
    }

    return item.text || '';
  };

  const isVisible = item.visible !== false;
  if (!isVisible) return null;

  const getBorderRadius = () => {
    if (item.type === 'circle') return '50%';

    // Individual corner radius values
    const topLeft = item.radiusTopLeft ?? item.radius ?? 0;
    const topRight = item.radiusTopRight ?? item.radius ?? 0;
    const bottomRight = item.radiusBottomRight ?? item.radius ?? 0;
    const bottomLeft = item.radiusBottomLeft ?? item.radius ?? 0;

    return `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`;
  };

  const borderRadius = getBorderRadius();

  // For line elements, render as SVG
  if (item.type === 'line') {
    return (
      <svg
        style={{
          position: 'absolute',
          left: item.x,
          top: item.y,
          width: Math.abs(item.w) || 1,
          height: Math.abs(item.h) || 1,
          opacity: item.opacity ?? 1,
          zIndex: item.z ?? 1,
          cursor: item.locked ? 'not-allowed' : 'move',
          overflow: 'visible',
        }}
        onMouseDown={(e) => onMouseDown(e, item)}
      >
        <line
          x1="0"
          y1="0"
          x2={item.w}
          y2={item.h}
          stroke={item.stroke || '#000000'}
          strokeWidth={item.strokeWidth || 2}
          strokeLinecap="round"
        />
        {isSelected && !item.locked && selectionCount === 1 && (
          <>
            <circle
              cx="0"
              cy="0"
              r="5"
              fill="var(--sp-orange)"
              className="sp-handle sp-handle--nw"
              style={{ cursor: 'nwse-resize' }}
            />
            <circle
              cx={item.w}
              cy={item.h}
              r="5"
              fill="var(--sp-orange)"
              className="sp-handle sp-handle--se"
              style={{ cursor: 'nwse-resize' }}
            />
          </>
        )}
      </svg>
    );
  }

  const baseStyle = {
    position: 'absolute',
    left: item.x,
    top: item.y,
    width: item.w,
    height: item.h,
    opacity: item.opacity ?? 1,
    zIndex: item.z ?? 1,
    borderRadius,
    background:
      item.type === 'text' ? 'transparent' : item.fill || 'transparent',
    borderWidth: item.type === 'image' ? 0 : (item.strokeWidth || 0) + 'px',
    borderStyle: item.type === 'image' ? 'none' : item.borderStyle || 'solid',
    borderColor:
      item.type === 'image' ? 'transparent' : item.stroke || 'transparent',
    cursor: item.locked ? 'not-allowed' : 'move',
    boxSizing: 'border-box',
    backgroundClip: 'padding-box',
    transform: `rotate(${item.rotation || 0}deg)`,
    transformOrigin: 'center center',
    ...(isSelected && item.type !== 'image'
      ? {
          outline: '2px solid var(--sp-orange)',
          outlineOffset: '-2px',
          boxShadow: '0 0 0 2px rgba(255,138,58,.18)',
        }
      : {}),
  };

  return (
    <div
      style={baseStyle}
      onMouseDown={(e) => onMouseDown(e, item)}
      onDoubleClick={(e) => {
        if (item.type === 'text' && !item.locked) {
          e.stopPropagation();
          onDoubleClick?.(item);
        }
      }}
    >
      {item.type === 'text' && (
        <div
          ref={textEditRef}
          style={{
            width: '100%',
            height: '100%',
            outline: 'none',
            color: item.color || '#111827',
            fontFamily: item.fontFamily || 'Marianne',
            fontSize: (item.fontSize || 24) + 'px',
            fontWeight: item.fontWeight || '400',
            whiteSpace: 'pre-wrap',
            overflow: 'hidden',
            padding: 4,
          }}
          contentEditable={!!item.editing}
          suppressContentEditableWarning
          onInput={(e) => {
            if (item.editing && textEditRef.current) {
              // Save cursor position before updating state
              const sel = window.getSelection();
              if (sel.rangeCount > 0) {
                const range = sel.getRangeAt(0);
                textEditRef.current._cursorPos = range.startOffset;
              }
              onTextChange?.(item, e.currentTarget.textContent);
            }
          }}
          onBlur={(e) => {
            if (item.editing) {
              onTextBlur?.(item);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && item.editing) {
              e.currentTarget.blur();
            }
          }}
        >
          {resolveText() || 'Double-cliquez pour éditer'}
        </div>
      )}
      {item.type === 'image' &&
        item.naturalW &&
        item.naturalH &&
        (() => {
          const fit = fitImage(
            item.naturalW,
            item.naturalH,
            item.w,
            item.h,
            'contain'
          );
          return (
            <>
              <img
                src={item.src}
                draggable={false}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  pointerEvents: 'none',
                  display: 'block',
                }}
              />
              {/* Selection frame on actual image bounds */}
              <div
                style={{
                  position: 'absolute',
                  left: fit.x,
                  top: fit.y,
                  width: fit.w,
                  height: fit.h,
                  borderWidth: '1px',
                  borderStyle: 'dashed',
                  borderColor: isSelected ? 'var(--sp-orange)' : 'transparent',
                  boxShadow: isSelected
                    ? '0 0 0 2px rgba(255,138,58,.18)'
                    : 'none',
                  boxSizing: 'border-box',
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              />
              {isSelected &&
                selectionCount === 1 &&
                !item.locked &&
                ['nw', 'ne', 'sw', 'se'].map((pos) => (
                  <div
                    key={pos}
                    className={`sp-handle sp-handle--${pos}`}
                    style={{
                      left:
                        (pos.includes('w') ? fit.x - 5 : fit.x + fit.w - 5) +
                        'px',
                      top:
                        (pos.includes('n') ? fit.y - 5 : fit.y + fit.h - 5) +
                        'px',
                    }}
                  />
                ))}
            </>
          );
        })()}
      {item.type === 'image' && !item.naturalW && (
        <img
          src={item.src}
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            pointerEvents: 'none',
            display: 'block',
          }}
        />
      )}
      {item.type !== 'image' &&
        isSelected &&
        selectionCount === 1 &&
        !item.locked &&
        ['nw', 'ne', 'sw', 'se'].map((pos) => (
          <div key={pos} className={`sp-handle sp-handle--${pos}`} />
        ))}
      {item.type !== 'line' &&
        isSelected &&
        selectionCount === 1 &&
        !item.locked && (
          <>
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '-30px',
                width: '1px',
                height: '30px',
                marginLeft: '0px',
                background: 'var(--sp-orange)',
                opacity: 0.5,
                pointerEvents: 'none',
              }}
            />
            <div
              className="sp-handle sp-handle--rotate"
              style={{
                position: 'absolute',
                left: '50%',
                top: '-30px',
                width: '10px',
                height: '10px',
                marginLeft: '-5px',
                background: 'var(--sp-orange)',
                borderRadius: '50%',
                cursor: 'grab',
                zIndex: 10,
              }}
            />
          </>
        )}
    </div>
  );
}

ElementNode.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    visible: PropTypes.bool,
    type: PropTypes.string,
    dbField: PropTypes.string,
    text: PropTypes.string,
    x: PropTypes.number,
    y: PropTypes.number,
    w: PropTypes.number,
    h: PropTypes.number,
    opacity: PropTypes.number,
    z: PropTypes.number,
    radius: PropTypes.number,
    radiusTopLeft: PropTypes.number,
    radiusTopRight: PropTypes.number,
    radiusBottomRight: PropTypes.number,
    radiusBottomLeft: PropTypes.number,
    fill: PropTypes.string,
    strokeWidth: PropTypes.number,
    borderStyle: PropTypes.string,
    stroke: PropTypes.string,
    locked: PropTypes.bool,
    editing: PropTypes.bool,
    color: PropTypes.string,
    fontFamily: PropTypes.string,
    fontSize: PropTypes.number,
    fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    naturalW: PropTypes.number,
    naturalH: PropTypes.number,
    src: PropTypes.string,
    rotation: PropTypes.number,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  selectionCount: PropTypes.number.isRequired,
  onMouseDown: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func,
  onTextChange: PropTypes.func,
  onTextBlur: PropTypes.func,
  db: PropTypes.shape({
    fields: PropTypes.arrayOf(PropTypes.string),
    records: PropTypes.array,
    currentRecord: PropTypes.number,
  }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Canvas
// ─────────────────────────────────────────────────────────────────────────────
export default function Canvas({ scheduleSave }) {
  const dispatch = useDispatch();
  const doc = useSelector(sel.doc);
  const currentPage = useSelector(sel.currentPage);
  const zoom = useSelector(sel.zoom);
  const selection = useSelector(sel.selection);
  const bgSelected = useSelector(sel.bgSelected);
  const showCropMarks = useSelector(sel.showCropMarks);
  const showRulers = useSelector(sel.showRulers);
  const db = useSelector(sel.db);

  const areaRef = useRef();
  const sizerRef = useRef();
  const shellRef = useRef();
  const rulerHRef = useRef();
  const rulerVRef = useRef();

  // ── Text editing handlers ──────────────────────────────────────────────────
  const handleTextDoubleClick = useCallback(
    (item) => {
      dispatch(
        studioActions.updateElement({
          id: item.id,
          changes: { editing: true },
        })
      );
    },
    [dispatch]
  );

  const handleTextInput = useCallback(
    (item, newText) => {
      dispatch(
        studioActions.updateElement({
          id: item.id,
          changes: { text: newText },
        })
      );
      // Auto-save after text changes (debounced by scheduleSave)
      scheduleSave();
    },
    [dispatch, scheduleSave]
  );

  const handleTextBlur = useCallback(
    (item) => {
      dispatch(
        studioActions.updateElement({
          id: item.id,
          changes: { editing: false },
        })
      );
      dispatch(studioActions.pushHistory());
      scheduleSave();
    },
    [dispatch, scheduleSave]
  );

  // ── Zoom / sizer ──────────────────────────────────────────────────────────
  const applyZoom = useCallback(
    (z, originX, originY) => {
      const area = areaRef.current;
      const zOld = zoom;
      const zNew = clamp(z, 0.1, 4);
      let anchorX, anchorY;

      if (area && originX !== undefined) {
        const relX = originX + area.scrollLeft;
        const relY = originY + area.scrollTop;
        anchorX = (relX - SIZER_PAD) / zOld;
        anchorY = (relY - SIZER_PAD) / zOld;
      }

      dispatch(studioActions.setZoomLevel(zNew));

      const naturalW = doc.widthPx + 64;
      const naturalH = doc.heightPx + 64;
      const sizerW = Math.round(naturalW * zNew) + SIZER_PAD * 2;
      const sizerH = Math.round(naturalH * zNew) + SIZER_PAD * 2;

      if (sizerRef.current) {
        sizerRef.current.style.width = sizerW + 'px';
        sizerRef.current.style.height = sizerH + 'px';
      }
      if (shellRef.current) {
        shellRef.current.style.left = SIZER_PAD + 'px';
        shellRef.current.style.top = SIZER_PAD + 'px';
        shellRef.current.style.transform = `scale(${zNew})`;
      }
      if (area && anchorX !== undefined) {
        area.scrollLeft = anchorX * zNew + SIZER_PAD - originX;
        area.scrollTop = anchorY * zNew + SIZER_PAD - originY;
      }

      if (showRulers) requestAnimationFrame(drawRulers);
    },
    [zoom, doc, showRulers, dispatch]
  );

  // Expose applyZoom to parent via window (used by keyboard hook)
  useEffect(() => {
    window.__studioApplyZoom = applyZoom;
  }, [applyZoom]);
  useEffect(() => {
    applyZoom(zoom);
  }, [doc.widthPx, doc.heightPx]); // re-sync on doc change

  // ── Rulers ────────────────────────────────────────────────────────────────
  const drawRulers = useCallback(() => {
    const area = areaRef.current;
    const shell = shellRef.current;
    const rH = rulerHRef.current;
    const rV = rulerVRef.current;
    if (!area || !shell || !rH || !rV) return;

    const areaRect = area.getBoundingClientRect();
    const shellRect = shell.getBoundingClientRect();
    const pageLeft = shellRect.left - areaRect.left + CANVAS_PAD * zoom;
    const pageTop = shellRect.top - areaRect.top + CANVAS_PAD * zoom;

    rH.width = areaRect.width;
    const ctxH = rH.getContext('2d');
    ctxH.clearRect(0, 0, rH.width, 20);
    ctxH.fillStyle = '#e8eaed';
    ctxH.fillRect(0, 0, rH.width, 20);
    drawRulerTicks(ctxH, 'h', rH.width, doc.widthPx, zoom, pageLeft);

    rV.height = areaRect.height;
    const ctxV = rV.getContext('2d');
    ctxV.clearRect(0, 0, 20, rV.height);
    ctxV.fillStyle = '#e8eaed';
    ctxV.fillRect(0, 0, 20, rV.height);
    drawRulerTicks(ctxV, 'v', rV.height, doc.heightPx, zoom, pageTop);
  }, [zoom, doc]);

  useEffect(() => {
    if (showRulers) requestAnimationFrame(drawRulers);
  }, [showRulers, zoom, drawRulers]);

  // ── Wheel zoom ────────────────────────────────────────────────────────────
  useEffect(() => {
    const area = areaRef.current;
    if (!area) return;
    const onWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const rect = area.getBoundingClientRect();
      applyZoom(
        zoom + (e.deltaY < 0 ? 0.1 : -0.1),
        e.clientX - rect.left,
        e.clientY - rect.top
      );
    };
    area.addEventListener('wheel', onWheel, { passive: false });
    return () => area.removeEventListener('wheel', onWheel);
  }, [applyZoom, zoom]);

  // ── Element drag/resize ───────────────────────────────────────────────────
  const handleElementMouseDown = useCallback(
    (e, item) => {
      if (item.locked) return;
      e.stopPropagation();
      dispatch(studioActions.setBgSelected(false));

      const handle = e.target.closest('.sp-handle');
      const isMulti = e.ctrlKey || e.metaKey || e.shiftKey;
      if (isMulti) {
        dispatch(
          studioActions.toggleSelection({ id: item.id, useRange: e.shiftKey })
        );
      } else if (!selection.includes(item.id)) {
        dispatch(studioActions.setSelection([item.id]));
      }

      const startX = e.clientX;
      const startY = e.clientY;
      const elements = currentPage.elements.filter((el) =>
        [...selection, item.id].includes(el.id)
      );

      // Build start rects (images: fit-rect based)
      const startRects = elements.map((it) => {
        if (it.type === 'image' && it.naturalW && it.naturalH) {
          const fit = fitImage(it.naturalW, it.naturalH, it.w, it.h, 'contain');
          return {
            id: it.id,
            x: it.x + fit.x,
            y: it.y + fit.y,
            w: fit.w,
            h: fit.h,
            isImg: true,
            nat: { w: it.naturalW, h: it.naturalH },
          };
        }
        return { id: it.id, x: it.x, y: it.y, w: it.w, h: it.h };
      });

      const corner = handle
        ? ['nw', 'ne', 'sw', 'se'].find((c) =>
            handle.classList.contains(`sp-handle--${c}`)
          )
        : null;
      const isRotating = handle?.classList.contains('sp-handle--rotate');
      const resizing = !!corner;

      // For rotation, store initial angle and rotation
      const initialRotation =
        isRotating && elements.length === 1 ? elements[0].rotation || 0 : 0;
      const start = startRects[0];
      const centerX = start ? start.x + start.w / 2 : 0;
      const centerY = start ? start.y + start.h / 2 : 0;

      // Get canvas page element to calculate proper coordinates
      const pageEl = document.getElementById('page');
      const pageRect = pageEl?.getBoundingClientRect();

      const getCanvasCoords = (clientX, clientY) => {
        if (!pageRect) return { x: 0, y: 0 };
        return {
          x: (clientX - pageRect.left) / zoom,
          y: (clientY - pageRect.top) / zoom,
        };
      };

      const startCoords = getCanvasCoords(startX, startY);
      const startAngle = isRotating
        ? Math.atan2(startCoords.y - centerY, startCoords.x - centerX)
        : 0;

      let hasMoved = false;

      const onMove = (ev) => {
        hasMoved = true;
        const dx = (ev.clientX - startX) / zoom;
        const dy = (ev.clientY - startY) / zoom;

        if (isRotating && elements.length === 1) {
          const el = elements[0];
          const currentCoords = getCanvasCoords(ev.clientX, ev.clientY);

          // Calculate angle from center to current mouse position
          const currentAngle = Math.atan2(
            currentCoords.y - centerY,
            currentCoords.x - centerX
          );
          const deltaAngle = (currentAngle - startAngle) * (180 / Math.PI);
          const newRotation = Math.round(initialRotation + deltaAngle);

          dispatch(
            studioActions.updateElement({
              id: el.id,
              changes: { rotation: newRotation },
            })
          );
        } else if (resizing && elements.length === 1) {
          const start = startRects[0];
          const el = elements[0];
          let x = start.x;
          let y = start.y;
          let w = start.w;
          let h = start.h;

          // Special handling for line elements
          if (el.type === 'line') {
            if (corner === 'nw') {
              // Moving start point
              x = start.x + dx;
              y = start.y + dy;
              w = start.w - dx;
              h = start.h - dy;
            } else if (corner === 'se') {
              // Moving end point
              w = start.w + dx;
              h = start.h + dy;
            }
            dispatch(
              studioActions.updateElement({
                id: el.id,
                changes: {
                  x: Math.round(x),
                  y: Math.round(y),
                  w: Math.round(w),
                  h: Math.round(h),
                },
              })
            );
          } else {
            // Original resize logic for other elements
            if (corner.includes('e')) w = clamp(start.w + dx, 10, 9999);
            if (corner.includes('s')) h = clamp(start.h + dy, 10, 9999);
            if (corner.includes('w')) {
              x = start.x + dx;
              w = clamp(start.w - dx, 10, 9999);
            }
            if (corner.includes('n')) {
              y = start.y + dy;
              h = clamp(start.h - dy, 10, 9999);
            }

            if (start.isImg) {
              const aspect = start.nat.w / start.nat.h;
              if (corner.includes('e') || corner.includes('w')) h = w / aspect;
              else w = h * aspect;
              dispatch(
                studioActions.updateElement({
                  id: el.id,
                  changes: {
                    x: Math.round(x),
                    y: Math.round(y),
                    w: Math.round(w),
                    h: Math.round(h),
                  },
                })
              );
            } else {
              dispatch(
                studioActions.updateElement({
                  id: el.id,
                  changes: { x, y, w, h },
                })
              );
            }
          }
        } else {
          dispatch(
            studioActions.updateElements(
              elements.map((el) => {
                const start = startRects.find((s) => s.id === el.id);
                return {
                  id: el.id,
                  changes: {
                    x: Math.round(start.x + dx),
                    y: Math.round(start.y + dy),
                  },
                };
              })
            )
          );
        }
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        if (hasMoved) {
          dispatch(studioActions.pushHistory());
          scheduleSave();
        }
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [dispatch, zoom, selection, currentPage, scheduleSave]
  );

  // ── Background drag ───────────────────────────────────────────────────────
  const handleBgMouseDown = useCallback(
    (e) => {
      const bg = currentPage.background;
      if (!bg || bg.locked) return;
      e.stopPropagation();
      dispatch(studioActions.setBgSelected(true));

      const startX = e.clientX;
      const startY = e.clientY;
      const startOX = bg.offsetX || 0;
      const startOY = bg.offsetY || 0;
      let hasMoved = false;

      const onMove = (ev) => {
        hasMoved = true;
        dispatch(
          studioActions.updateBackground({
            ...bg,
            offsetX: Math.round(startOX + (ev.clientX - startX) / zoom),
            offsetY: Math.round(startOY + (ev.clientY - startY) / zoom),
          })
        );
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        if (hasMoved) {
          scheduleSave();
        }
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [currentPage, dispatch, zoom, scheduleSave]
  );

  if (!currentPage) return null;

  const bg = currentPage.background;

  return (
    <main className="sp-workspace" id="workspace">
      <div
        className={`sp-workspace-inner${
          showRulers ? ' sp-workspace-inner--rulers' : ''
        }`}
        id="workspaceInner"
      >
        {showRulers && <div className="sp-ruler-corner" />}
        {showRulers && (
          <canvas
            ref={rulerHRef}
            className="sp-ruler-h"
            height="20"
            id="rulerH"
          />
        )}
        {showRulers && (
          <canvas
            ref={rulerVRef}
            className="sp-ruler-v"
            width="20"
            id="rulerV"
          />
        )}

        <div
          className="sp-canvas-area"
          id="canvasArea"
          ref={areaRef}
          onScroll={() => showRulers && requestAnimationFrame(drawRulers)}
        >
          <div className="sp-canvas-sizer" id="canvasSizer" ref={sizerRef}>
            <div className="sp-canvas-shell" id="canvasShell" ref={shellRef}>
              {/* Background */}
              {bg?.src && (
                <div
                  className={`sp-bg-layer${
                    bgSelected ? ' sp-bg-layer--selected' : ''
                  }`}
                  style={{
                    opacity: bg.visible === false ? 0 : bg.opacity ?? 1,
                    pointerEvents: bg.locked ? 'none' : 'auto',
                  }}
                  onMouseDown={handleBgMouseDown}
                >
                  <img
                    src={bg.src}
                    draggable={false}
                    style={{
                      position: 'absolute',
                      left: (bg.offsetX || 0) + 'px',
                      top: (bg.offsetY || 0) + 'px',
                      width: '100%',
                      height: '100%',
                      objectFit:
                        bg.mode === 'stretch' ? 'fill' : bg.mode || 'contain',
                      transform: `scale(${(bg.scale || 100) / 100})`,
                      transformOrigin: 'center',
                      display: 'block',
                    }}
                  />
                </div>
              )}

              {/* Page */}
              <div
                className="sp-page"
                style={{
                  width: doc.widthPx,
                  height: doc.heightPx,
                  pointerEvents: bgSelected ? 'none' : 'auto',
                }}
                id="page"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) {
                    dispatch(studioActions.clearSelection());
                    // Stop editing when clicking outside
                    const editingElements = currentPage.elements.filter(
                      (el) => el.editing
                    );
                    editingElements.forEach((el) => {
                      dispatch(
                        studioActions.updateElement({
                          id: el.id,
                          changes: { editing: false },
                        })
                      );
                    });
                  }
                }}
              >
                {currentPage.elements.map((item) => (
                  <ElementNode
                    key={item.id}
                    item={item}
                    isSelected={selection.includes(item.id)}
                    selectionCount={selection.length}
                    onMouseDown={handleElementMouseDown}
                    onDoubleClick={handleTextDoubleClick}
                    onTextChange={handleTextInput}
                    onTextBlur={handleTextBlur}
                    db={db}
                  />
                ))}
              </div>

              {/* Crop marks overlay */}
              {showCropMarks && (
                <div className="sp-crop-overlay">
                  {[
                    {
                      hx: CANVAS_PAD - 2,
                      hy: CANVAS_PAD - 2,
                      vx: CANVAS_PAD - 2,
                      vy: CANVAS_PAD - 2,
                    },
                    {
                      hx: CANVAS_PAD + doc.widthPx - 23,
                      hy: CANVAS_PAD - 2,
                      vx: CANVAS_PAD + doc.widthPx,
                      vy: CANVAS_PAD - 2,
                    },
                    {
                      hx: CANVAS_PAD - 2,
                      hy: CANVAS_PAD + doc.heightPx,
                      vx: CANVAS_PAD - 2,
                      vy: CANVAS_PAD + doc.heightPx - 23,
                    },
                    {
                      hx: CANVAS_PAD + doc.widthPx - 23,
                      hy: CANVAS_PAD + doc.heightPx,
                      vx: CANVAS_PAD + doc.widthPx,
                      vy: CANVAS_PAD + doc.heightPx - 23,
                    },
                  ].map((p, i) => (
                    <React.Fragment key={i}>
                      <div
                        className="sp-crop sp-crop--h"
                        style={{ left: p.hx, top: p.hy }}
                      />
                      <div
                        className="sp-crop sp-crop--v"
                        style={{ left: p.vx, top: p.vy }}
                      />
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

Canvas.propTypes = {
  scheduleSave: PropTypes.func.isRequired,
};
